// An easy-to-use modal prompt that has
// 1) A localized message
// 2) Zero or more buttons
// It can be dismissed by escape (or not), enter, or by clicking a button.
// The text on the buttons can be customized.
// To use it, dispatch showModalPrompt from the modalPromptReducer, and pass
// properties.
//
// dispatch(showModalPrompt({
//   message: { id: 'message-id', defaultMessage: 'Hello, {friend}' },
//   messageArguments: { friend: 'David' },
//   button1text: { id: 'cancel-id', defaultMessage: 'Cancel' } // Leave this out for default (Cancel)
//   button2text: { id: 'ok-id', defaultMessage: 'OK' } // Leave this out for default (OK)
//   onButton1: anOptionalFunction,
//   onButton2: anotherOptionalFunction,
//   closeOnEscape: true,
//   isButton1Default: false, // Yes, they can both be default, or neither can.
//   isButton2Default: true
// }));
//
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import Modal from 'react-modal';
import { actions } from 'portfolio-shared/state/reducers/modalPromptReducer';
import localizedStrings from 'portfolio-shared/util/localizedStrings';
import 'portfolio-shared/view/ModalPrompt/ModalPrompt.css';

const { hideModalPrompt } = actions;

const kButton1Id = 'modalPromptButton1';
const kButton2Id = 'modalPromptButton2';

class ModalPrompt extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    isPanelOpen: PropTypes.bool.isRequired,
    hideModalPrompt: PropTypes.func.isRequired,
    message: PropTypes.object,
    messageArguments: PropTypes.object,
    button1text: PropTypes.object,
    button2text: PropTypes.object,
    onButton1: PropTypes.func,
    onButton2: PropTypes.func,
    closeOnEscape: PropTypes.bool,
    isButton1Default: PropTypes.bool.isRequired,
    isButton2Default: PropTypes.bool.isRequired,
    contentStyle: PropTypes.object,
    dataTestName: PropTypes.string,
  };

  constructor() {
    super();
    this.onButton1 = this.onButton1.bind(this);
    this.onButton2 = this.onButton2.bind(this);
    this.onRequestClose = this.onRequestClose.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.onAfterOpen = this.onAfterOpen.bind(this);
    this.linesToParagraphs = this.linesToParagraphs.bind(this);
  }

  onButton1() {
    this.props.hideModalPrompt();
    if (this.props.onButton1) {
      this.props.onButton1();
    }
  }

  onButton2() {
    this.props.hideModalPrompt();
    if (this.props.onButton2) {
      this.props.onButton2();
    }
  }

  // this is called when the esc key is clicked
  onRequestClose() {
    if (this.props.closeOnEscape !== false) {
      this.props.hideModalPrompt();
    }
  }

  handleKeyDown(e) {
    if (e.keyCode === 13) {
      if (this.props.isButton1Default) {
        this.onButton1();
      } else if (this.props.isButton2Default) {
        this.onButton2();
      }
    }
  }

  onAfterOpen() {
    if (this.props.isButton1Default) {
      document.getElementById(kButton1Id).focus();
    } else if (this.props.isButton2Default) {
      document.getElementById(kButton2Id).focus();
    }
  }

  render() {
    // TODO: move me later
    const modalStyles = {
      overlay: {
        zIndex: 9998,
        backgroundColor: 'rgba(127, 127, 127, 0.9)',
      },
      content: {
        position: 'absolute',
        top: '45%',
        left: '50%',
        maxWidth: '400px',
        minWidth: '250px',
        backgroundColor: '#fff',
        margin: '30px auto auto',
        ...this.props.contentStyle,
      },
    };
    return (
      <Modal
        style={modalStyles}
        className="modal-prompt-panel"
        contentLabel="Modal Prompt"
        onRequestClose={this.onRequestClose}
        shouldCloseOnOverlayClick={false}
        closeTimeoutMS={150}
        isOpen={this.props.isPanelOpen}
        onAfterOpen={this.onAfterOpen}
        shouldReturnFocusAfterClose={false}
      >
        <div
          className="modal-prompt-panelBody"
          data-test-name={this.props.dataTestName || 'modal-prompt'}
        >
          {this.linesToParagraphs()}
        </div>
        <div className="modal-prompt-panelButtonsContainer buttons">
          {this.props.button1text ? (
            <input
              id={kButton1Id}
              className={
                this.props.isButton1Default ? 'button is-primary' : 'button'
              }
              type="button"
              onClick={this.onButton1}
              value={this.props.intl.formatMessage(this.props.button1text)}
            />
          ) : (
            ''
          )}
          {this.props.button2text ? (
            <input
              id={kButton2Id}
              className={
                this.props.isButton2Default ? 'button is-primary' : 'button'
              }
              type="button"
              onClick={this.onButton2}
              value={this.props.intl.formatMessage(this.props.button2text)}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>
    );
  }

  linesToParagraphs() {
    if (this.props.message) {
      const string = this.props.intl.formatMessage(
        this.props.message,
        this.props.messageArguments
      );
      const lines = string.split('\n');
      return lines.map((line, index) => {
        const key = 'line' + index;
        if (line.length === 0) {
          return <div key={key}>&nbsp;</div>;
        } else {
          return (
            <div className="modal-prompt-panelLine" key={key}>
              {line}
            </div>
          );
        }
      });
    }
  }
}

let wrappedForm = injectIntl(ModalPrompt);

export default (wrappedForm = connect(
  state => {
    const props = {
      isPanelOpen: state.modalPrompt.isVisible,
      ...state.modalPrompt.properties, // These were set by the showModalPrompt action.
    };
    if (props.button1text === undefined) {
      props.button1text = localizedStrings.Cancel;
    }
    if (props.button2text === undefined) {
      props.button2text = localizedStrings.OK;
    }
    if (props.isButton1Default === undefined) {
      props.isButton1Default = false;
    }
    if (props.isButton2Default === undefined) {
      props.isButton2Default = false;
    }
    return props;
  },
  dispatch =>
    bindActionCreators(
      {
        hideModalPrompt,
      },
      dispatch
    )
)(wrappedForm));



// WEBPACK FOOTER //
// ./src/view/App/DialogPanels/ModalPrompt.js